import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  completeBqMockSession,
  getBqMockSession,
  listBqMockSessions,
  startBqMockSession,
  submitBqMockAnswer,
  transcribeBqMockAudio,
} from '../../services/bqMockService';
import { useAudioRecorder } from './useAudioRecorder';
import './BQMock.css';

const QUESTION_COUNT = 3;

const MOCK_MODES = [
  { id: 'QUICK', label: 'Quick mode', description: 'A quick mock on STAR principles.' },
  { id: 'COMPLETE', label: 'Complete mode', description: 'Pick from all question sets.' },
  { id: 'IMPROVE', label: 'Improve mode', description: 'Focus on your historical weak area.' },
];

const MOCK_RULES = [
  '3 questions per round — answer with STAR (Situation, Task, Action, Result).',
  'Practice feedback only; not an official evaluation or hiring decision.',
  'Do not include confidential information in your answer in case they violate the non-disclosure agreement.',
];

const STAR_KEYS = [
  ['situation', 'Situation'],
  ['task', 'Task'],
  ['action', 'Action'],
  ['result', 'Result'],
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function modeLabel(mode) {
  const labels = {
    QUICK: 'Quick mode',
    IMPROVE: 'Improve mode',
    COMPLETE: 'Complete mode',
    TARGETED: 'Targeted drill',
    FUNCTIONAL: 'Functional',
  };
  return labels[mode] || mode || 'Quick mode';
}

function StarGrid({ star }) {
  if (!star) return null;
  return (
    <div className="bq-mock__star-grid">
      {STAR_KEYS.map(([key, label]) => {
        const item = star[key];
        if (!item) return null;
        return (
          <div key={key} className="bq-mock__star-item">
            <div className="bq-mock__star-label">
              {label} · {item.status || '—'}
            </div>
            <div>{item.note || '—'}</div>
          </div>
        );
      })}
    </div>
  );
}

function FeedbackPanel({ feedback }) {
  if (!feedback) return null;
  const rating = feedback.overallRating || 'Mixed';
  return (
    <div className="bq-mock__feedback">
      <span className={`bq-mock__rating bq-mock__rating--${rating}`}>{rating}</span>
      <StarGrid star={feedback.star} />
      {feedback.summary && <p className="bq-mock__summary">{feedback.summary}</p>}
      {feedback.improvements?.length > 0 && (
        <ul className="bq-mock__list">
          {feedback.improvements.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QuestionBlock({ sortOrder, questionCount, topicTitle, questionText, labelPrefix = 'Question' }) {
  return (
    <div className="bq-mock__question-block">
      <div className="bq-mock__question-meta">
        <span className="bq-mock__question-badge">
          {labelPrefix} {sortOrder}
          {questionCount != null ? ` of ${questionCount}` : ''}
        </span>
        <span className="bq-mock__topic">{topicTitle}</span>
      </div>
      <p className="bq-mock__question">{questionText}</p>
    </div>
  );
}

function BuyBqSetModal({ onClose }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="bq-mock-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="bq-mock-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bq-buy-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bq-mock-modal__head">
          <span className="bq-mock-modal__eyebrow">Annual access</span>
          <h2 id="bq-buy-modal-title">Own the FULL BQ question bank</h2>
          <button type="button" className="bq-mock-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="bq-mock-modal__lead">
          Unlock our <strong>complete, genuine </strong>behavioral interview question set — the same depth candidates use to
          prepare for full-time BQ loops.
        </p>

        <ul className="bq-mock-modal__highlights">
          <li>
            <strong>Unlimited AI mock sessions</strong> — Quick, Complete, and Improve modes with STAR feedback
          </li>
          <li>
            <strong>~250 real-world questions</strong> collected from all major companies
          </li>
          <li>
            <strong>Broad full-time coverage</strong> — practice nearly every BQ theme you are likely to see
          </li>
          <li>
            <strong>Full year of access</strong> — browse, drill, and revisit the complete question set anytime
          </li>
        </ul>

        <div className="bq-mock-modal__price">
          <span className="bq-mock-modal__price-amount">$1,499</span>
          <span className="bq-mock-modal__price-term">/ year · unlimited mock</span>
        </div>

        <p className="bq-mock-modal__cta-text">
          Ready to upgrade? Contact your <strong>system administrator</strong> to purchase annual access.
          We will enable the full question bank and unlimited mock sessions on your account.
        </p>

        <div className="bq-mock-modal__actions">
          <button type="button" className="bq-mock__btn bq-mock__btn--primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BQMock() {
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState('QUICK');
  const [draft, setDraft] = useState('');
  const [inputMode, setInputMode] = useState('text');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState('');
  const [viewingFeedback, setViewingFeedback] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const questionStartedAt = useRef(null);
  const { isSupported: voiceSupported, isRecording, startRecording, stopRecording } = useAudioRecorder();

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await listBqMockSessions();
      setHistory(data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const currentQuestion = useMemo(() => {
    if (!session?.questions?.length) return null;
    if (session.status === 'COMPLETED') return null;
    return session.questions.find((q) => !q.answered) || null;
  }, [session]);

  const lastAnswered = useMemo(() => {
    if (!session?.questions?.length) return null;
    const answered = session.questions.filter((q) => q.answered);
    return answered.length ? answered[answered.length - 1] : null;
  }, [session]);

  const inProgressSessions = useMemo(
    () => history.filter((s) => s.status === 'IN_PROGRESS'),
    [history]
  );

  const busy = loading || transcribing;

  useEffect(() => {
    if (currentQuestion && !viewingFeedback) {
      questionStartedAt.current = Date.now();
    }
  }, [currentQuestion?.answerId, viewingFeedback]);

  const handleStart = async () => {
    setError('');
    setLoading(true);
    setViewingFeedback(false);
    setDraft('');
    setInputMode('text');
    try {
      const data = await startBqMockSession({
        questionCount: QUESTION_COUNT,
        mode,
      });
      setSession(data);
      await loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Could not start mock session.');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (sessionId) => {
    setError('');
    setLoading(true);
    setViewingFeedback(false);
    setDraft('');
    setInputMode('text');
    try {
      const data = await getBqMockSession(sessionId);
      setSession(data);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Could not resume session.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!session || !currentQuestion) return;
    if (draft.trim().length < 40) {
      setError('Please write at least a few sentences (40+ characters) using the STAR format.');
      return;
    }
    const durationSeconds = questionStartedAt.current
      ? Math.max(0, Math.round((Date.now() - questionStartedAt.current) / 1000))
      : undefined;
    setError('');
    setLoading(true);
    try {
      const data = await submitBqMockAnswer(
        session.id,
        currentQuestion.answerId,
        draft.trim(),
        durationSeconds
      );
      setSession(data);
      setDraft('');
      setViewingFeedback(true);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Could not submit answer.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setViewingFeedback(false);
    if (!session) return;
    const remaining = session.questions.filter((q) => !q.answered);
    if (remaining.length > 0) return;

    setLoading(true);
    setError('');
    try {
      const data = await completeBqMockSession(session.id);
      setSession(data);
      await loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Could not complete session.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isRecording) stopRecording();
    setSession(null);
    setDraft('');
    setInputMode('text');
    setViewingFeedback(false);
    setError('');
    loadHistory();
  };

  const readyToComplete = useMemo(() => {
    if (!session || session.status === 'COMPLETED') return false;
    return session.answeredCount >= session.questionCount;
  }, [session]);

  const showLanding = !session;
  const showComplete = session?.status === 'COMPLETED';
  const showFeedbackStep = viewingFeedback && lastAnswered && !showComplete && !readyToComplete;
  const showQuestionStep = session && currentQuestion && !showFeedbackStep && !showComplete && !readyToComplete;
  const showCompletePrompt = readyToComplete && !showComplete && !showFeedbackStep;

  return (
    <div className="bq-mock">
      <div className="bq-mock__header">
        <h1 className="bq-mock__title">BQ Mock</h1>
        <button type="button" className="bq-mock__buy-cta" onClick={() => setShowBuyModal(true)}>
          Buy all genuine BQ question set
        </button>
      </div>
      {showBuyModal && <BuyBqSetModal onClose={() => setShowBuyModal(false)} />}
      {error && <div className="bq-mock__error">{error}</div>}

      {showLanding && (
        <>
          <p className="bq-mock__lead">
            Practice a short behavioral interview round with 3 problems.
          </p>
          <ul className="bq-mock__rules">
            {MOCK_RULES.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>

          <div className="bq-mock__mode-row">
            {MOCK_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`bq-mock__mode-btn ${mode === m.id ? 'bq-mock__mode-btn--active' : ''}`}
                disabled={loading}
                onClick={() => setMode(m.id)}
              >
                <span className="bq-mock__mode-btn-title">{m.label}</span>
                <span className="bq-mock__mode-btn-desc">{m.description}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="bq-mock__btn bq-mock__btn--primary"
            disabled={loading}
            onClick={handleStart}
          >
            {loading ? 'Starting…' : 'Start session'}
          </button>

          {(historyLoading || inProgressSessions.length > 0 || history.length > 0) && (
            <div className="bq-mock__history">
              <h2 className="bq-mock__history-title">Your sessions</h2>
              {historyLoading && <p className="bq-mock__hint">Loading history…</p>}
              {!historyLoading && inProgressSessions.length > 0 && (
                <div className="bq-mock__history-section">
                  <p className="bq-mock__section-label">Continue in progress</p>
                  <ul className="bq-mock__history-list">
                    {inProgressSessions.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          className="bq-mock__history-item bq-mock__history-item--resume"
                          disabled={loading}
                          onClick={() => handleResume(s.id)}
                        >
                          <span>
                            {modeLabel(s.mode)} · {s.answeredCount}/{s.questionCount} answered
                          </span>
                          <span className="bq-mock__history-meta">{formatDate(s.createdAt)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!historyLoading && history.filter((s) => s.status === 'COMPLETED').length > 0 && (
                <div className="bq-mock__history-section">
                  <p className="bq-mock__section-label">Past sessions</p>
                  <ul className="bq-mock__history-list">
                    {history
                      .filter((s) => s.status === 'COMPLETED')
                      .slice(0, 10)
                      .map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            className="bq-mock__history-item"
                            disabled={loading}
                            onClick={() => handleResume(s.id)}
                          >
                            <span>
                              {modeLabel(s.mode)} · {s.questionCount} questions
                            </span>
                            <span className="bq-mock__history-meta">{formatDate(s.completedAt || s.createdAt)}</span>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showQuestionStep && (
        <div className="bq-mock__card">
          <QuestionBlock
            sortOrder={currentQuestion.sortOrder}
            questionCount={session.questionCount}
            topicTitle={currentQuestion.topicTitle}
            questionText={currentQuestion.questionText}
          />

          <div className="bq-mock__input-toolbar">
            <div className="bq-mock__pill-group" role="tablist" aria-label="Answer input mode">
              <button
                type="button"
                role="tab"
                aria-selected={inputMode === 'text'}
                className={`bq-mock__pill ${inputMode === 'text' ? 'bq-mock__pill--active' : ''}`}
                onClick={() => setInputMode('text')}
                disabled={busy || isRecording}
              >
                Type
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={inputMode === 'voice'}
                className={`bq-mock__pill ${inputMode === 'voice' ? 'bq-mock__pill--active' : ''}`}
                onClick={() => setInputMode('voice')}
                disabled={busy || isRecording || !voiceSupported}
                title={voiceSupported ? '' : 'Voice input is not supported in this browser'}
              >
                Speak
              </button>
            </div>

            {voiceSupported ? (
              <VoiceControls
                busy={busy}
                isRecording={isRecording}
                transcribing={transcribing}
                inputMode={inputMode}
                startRecording={startRecording}
                stopRecording={stopRecording}
                sessionId={session.id}
                answerId={currentQuestion.answerId}
                onTranscript={(text) => setDraft((prev) => (prev ? `${prev}\n\n${text}` : text))}
                onError={setError}
                setTranscribing={setTranscribing}
              />
            ) : (
              <span className="bq-mock__voice-unavailable">Voice unavailable</span>
            )}
          </div>

          {!voiceSupported && inputMode === 'voice' && (
            <p className="bq-mock__hint">Voice input is not available in this browser. Use Type instead.</p>
          )}

          {isRecording && <p className="bq-mock__recording-dot">Recording</p>}

          <textarea
            className="bq-mock__textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Describe a specific example using STAR principles. What was the situation, your goal, what you did, and the outcome?"
            disabled={busy || isRecording}
          />
          <p className="bq-mock__hint">Aim for 400 words.</p>
          <div className="bq-mock__actions" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="bq-mock__btn bq-mock__btn--primary"
              disabled={busy || isRecording}
              onClick={handleSubmitAnswer}
            >
              {loading ? 'Evaluating…' : 'Submit answer'}
            </button>
            <button type="button" className="bq-mock__btn bq-mock__btn--secondary" disabled={busy || isRecording} onClick={handleReset}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showFeedbackStep && (
        <div className="bq-mock__card">
          <QuestionBlock
            sortOrder={lastAnswered.sortOrder}
            questionCount={session.questionCount}
            topicTitle={lastAnswered.topicTitle}
            questionText={lastAnswered.questionText}
            labelPrefix="Feedback · Question"
          />
          <FeedbackPanel feedback={lastAnswered.feedback} />
          <div className="bq-mock__actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="bq-mock__btn bq-mock__btn--primary" onClick={handleNext}>
              {session.questions.some((q) => !q.answered) ? 'Next question' : 'View session summary'}
            </button>
          </div>
        </div>
      )}

      {showCompletePrompt && (
        <div className="bq-mock__card">
          <p className="bq-mock__summary">You&apos;ve answered all questions in this session.</p>
          <div className="bq-mock__actions">
            <button type="button" className="bq-mock__btn bq-mock__btn--primary" disabled={loading} onClick={handleNext}>
              {loading ? 'Finishing…' : 'View session summary'}
            </button>
            <button type="button" className="bq-mock__btn bq-mock__btn--secondary" disabled={loading} onClick={handleReset}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showComplete && (
        <div className="bq-mock__card">
          <h2 style={{ marginTop: 0 }}>Session complete</h2>
          <p className="bq-mock__summary">{session.summary}</p>
          {session.focusTopics?.length > 0 && (
            <div className="bq-mock__focus-topics">
              <p className="bq-mock__section-label">Focus areas for next practice</p>
              <ul className="bq-mock__focus-list">
                {session.focusTopics.map((topic) => (
                  <li key={topic}>{topic}</li>
                ))}
              </ul>
            </div>
          )}
          {session.questions.map((q) => (
            <div key={q.answerId} className="bq-mock__review-item">
              <QuestionBlock
                sortOrder={q.sortOrder}
                topicTitle={q.topicTitle}
                questionText={q.questionText}
                labelPrefix="Question"
              />
              <FeedbackPanel feedback={q.feedback} />
            </div>
          ))}
          <div className="bq-mock__actions" style={{ marginTop: '1.25rem' }}>
            <button type="button" className="bq-mock__btn bq-mock__btn--primary" onClick={handleReset}>
              Start another mock
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VoiceControls({
  busy,
  isRecording,
  transcribing,
  inputMode,
  startRecording,
  stopRecording,
  sessionId,
  answerId,
  onTranscript,
  onError,
  setTranscribing,
}) {
  const recordingPromiseRef = React.useRef(null);

  const handleClick = async () => {
    if (inputMode !== 'voice' || busy) return;
    onError('');

    if (!isRecording) {
      recordingPromiseRef.current = startRecording().catch((e) => {
        onError(e.message || 'Microphone access was denied.');
        return null;
      });
      return;
    }

    stopRecording();
    setTranscribing(true);
    try {
      const blobPromise = recordingPromiseRef.current;
      recordingPromiseRef.current = null;
      if (!blobPromise) {
        onError('Recording failed. Please try again.');
        return;
      }
      const blob = await blobPromise;
      if (!blob || blob.size < 1000) {
        onError('Recording is too short. Please try again.');
        return;
      }
      const ext = blob.type.includes('mp4') ? 'recording.m4a' : 'recording.webm';
      const { text } = await transcribeBqMockAudio(blob, ext, sessionId, answerId);
      if (!text?.trim()) {
        onError('Could not transcribe audio. Please try again.');
        return;
      }
      onTranscript(text.trim());
    } catch (e) {
      onError(e.response?.data?.error || e.message || 'Transcription failed.');
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <button
      type="button"
      className={`bq-mock__btn bq-mock__btn--record ${isRecording ? 'bq-mock__btn--record-stop' : ''}`}
      disabled={(busy && !isRecording) || inputMode !== 'voice'}
      onClick={handleClick}
    >
      {transcribing ? 'Transcribing…' : isRecording ? 'Stop recording' : 'Start recording'}
    </button>
  );
}
